import { request } from "./http.js";

/**
 * Share links and guest names (g69), for the share dialog (g73) and the shared
 * page (g72). Like `auth.ts`, this is a surface of its own rather than a
 * screen's business: no view calls `fetch`, and nothing here touches the
 * mirror. A shared list's data will still come through the store and
 * `/api/sync` (g70, g71).
 *
 * The token is a credential. It is held only by the caller that read it from
 * the page's fragment, travels only in the `X-Trayflux-Share` header, and is
 * never put in a URL, a body, storage or a log here.
 */

export const SHARE_HEADER = "X-Trayflux-Share";

export type ShareMode = "view" | "edit";

/** One mode of a list's sharing, as its owner sees it. */
export type ShareState =
  | { active: true; url: string; generation: string; createdAt: number }
  | { active: false };

export interface ShareStatus {
  listId: string;
  view: ShareState;
  edit: ShareState;
}

/** Both modes of one of the owner's lists. Reading never creates a link. */
export function shareStatus(listId: string): Promise<ShareStatus> {
  return request<ShareStatus>(`/api/shares/${encodeURIComponent(listId)}`);
}

/** Turns a mode on. When it is on already the same link comes back, unchanged. */
export async function enableShare(listId: string, mode: ShareMode): Promise<ShareState> {
  const body = await request<{ share: ShareState }>(`/api/shares/${encodeURIComponent(listId)}/${mode}`, {
    method: "POST",
    body: "{}",
  });
  return body.share;
}

/**
 * Replaces a mode's link. `replaces` is the generation the owner was shown;
 * if it changed since, this rejects with `share_changed` and nothing changes.
 */
export async function regenerateShare(listId: string, mode: ShareMode, replaces: string): Promise<ShareState> {
  const body = await request<{ share: ShareState }>(`/api/shares/${encodeURIComponent(listId)}/${mode}/regenerate`, {
    method: "POST",
    body: JSON.stringify({ replaces }),
  });
  return body.share;
}

/** Turns a mode off. The other mode keeps working. */
export async function disableShare(listId: string, mode: ShareMode): Promise<void> {
  await request(`/api/shares/${encodeURIComponent(listId)}/${mode}`, { method: "DELETE" });
}

const TOKEN = /^tfs_[A-Za-z0-9_-]{22}\.[a-z0-9]{1,16}\.[A-Za-z0-9_-]{43}$/;

/**
 * The grant a token names: the part of a link that a link made since g131
 * also carries in its path. Not the secret; the MAC is.
 */
export function shareGrantId(token: string): string {
  return token.slice("tfs_".length, "tfs_".length + 22);
}

/** The token in a shared page's fragment, or undefined when it is not one. */
export function shareTokenFromHash(hash: string): string | undefined {
  const token = hash.startsWith("#") ? hash.slice(1) : hash;
  return TOKEN.test(token) ? token : undefined;
}

/** Who is looking at a shared list. `creatorId` is opaque; nothing here is an account id. */
export interface ShareVisitor {
  kind: "account" | "guest" | "anonymous";
  creatorId: string | null;
  name: string | null;
  /** An edit link with no name yet: ask for one before the first write. */
  needsName: boolean;
  /** A session cookie was sent and no longer works: offer to sign in again. */
  sessionExpired?: true;
}

/**
 * Whether the list is in the visitor's Lists (g132): their own, added through
 * this link, or neither. Anyone not signed in reads `addable`. Absent from a
 * server older than g132, where nothing can be added.
 */
export type Listed = "owner" | "added" | "addable";

export interface ResolvedShare {
  list: { id: string; name: string; kind: "todo" | "shopping" | "other" };
  permission: ShareMode;
  generation: string;
  visitor: ShareVisitor;
  listed?: Listed;
}

/** The request header an added list is opened by (g132): its grant id, beside the session. */
export const ADDED_HEADER = "X-Trayflux-Added";

/**
 * What a shared list's requests carry: the link, or for a list the visitor
 * added to their Lists (g132), its grant id beside the account's session.
 */
export type ShareCredential =
  | { kind: "link"; /** Memory only. Travels in `X-Trayflux-Share` and nowhere else. */ token: string }
  | { kind: "added"; grantId: string };

function shareHeaders(token: string): Record<string, string> {
  return { [SHARE_HEADER]: token };
}

/**
 * The one header a shared list's requests carry: the link, or an added list's
 * grant id, never both. An added list never sends a link, even where this
 * device holds one.
 */
export function credentialHeaders(credential: ShareCredential): Record<string, string> {
  return credential.kind === "link" ? shareHeaders(credential.token) : { [ADDED_HEADER]: credential.grantId };
}

/**
 * What a link or an added list opens, and who is looking. One that opens
 * nothing rejects with `share_unavailable`, the same for every reason: stop
 * retrying. An added list the account does not hold rejects with `not_added`,
 * and one opened signed out with a 401.
 */
export function resolveShare(credential: ShareCredential): Promise<ResolvedShare> {
  return request<ResolvedShare>("/api/shared/resolve", { headers: credentialHeaders(credential) });
}

/**
 * Adds the link's list to the signed-in visitor's Lists (g132). Rejects with
 * `sign_in_required` for anyone not signed in, and `own_list` for its owner.
 */
export async function addToMyLists(token: string): Promise<void> {
  await request("/api/shared/add", { method: "POST", headers: shareHeaders(token), body: "{}" });
}

/**
 * Holds a guest's Add to my Lists across the sign-in they are about to make
 * (g132). The server keeps the grant id, never the link, and the sign-in that
 * finishes next adds it.
 */
export async function holdAddForSignIn(token: string): Promise<void> {
  await request("/api/shared/add-intent", { method: "POST", headers: shareHeaders(token), body: "{}" });
}

/** The guest went back without signing in: a later sign-in adds nothing. */
export async function dropAddForSignIn(): Promise<void> {
  await request("/api/shared/add-intent", { method: "DELETE" });
}

/**
 * Gives or changes a guest's name. The first name, from an edit link, makes
 * the guest this browser remembers on every shared list. A signed-in visitor
 * names their account with `updateDisplayName` in `auth.ts` instead; this
 * rejects them with `account_identity`.
 */
export async function setGuestName(token: string, displayName: string): Promise<ShareVisitor> {
  const body = await request<{ visitor: ShareVisitor }>("/api/shared/identity", {
    method: "POST",
    headers: shareHeaders(token),
    body: JSON.stringify({ displayName }),
  });
  return body.visitor;
}
